// Musically correct ABC transposition, done on the ABC TEXT itself.
//
// Why not abcjs.strTranspose? It maps characters by offset and drifts on real
// session tunes (variant endings "|1", decorations like "~c3", CRLF line
// endings…), silently leaving some notes untransposed. Here we read every
// note's SOUNDING pitch (key signature + measure accidentals), shift it by the
// requested semitones and respell it in the destination key — like a copyist.

const LETTERS = ["C", "D", "E", "F", "G", "A", "B"];
const LETTER_IDX = { C: 0, D: 1, E: 2, F: 3, G: 4, A: 5, B: 6 };
const PC = [0, 2, 4, 5, 7, 9, 11];
// fifths of the MAJOR key built on each natural letter
const MAJOR_FIFTHS = [0, 2, 4, -1, 1, 3, 5];
// how far each mode sits from its relative major on the circle of fifths
const MODE_FIFTHS = {
  "": 0, maj: 0, ion: 0, lyd: 1, mix: -1, dor: -2,
  min: -3, m: -3, aeo: -3, phr: -4, loc: -5,
};
const SHARP_ORDER = [3, 0, 4, 1, 5, 2, 6]; // F C G D A E B
const FLAT_ORDER = [6, 2, 5, 1, 4, 0, 3]; // B E A D G C F
const ACC_VAL = { "^^": 2, "^": 1, "=": 0, _: -1, __: -2 };
const ACC_STR = { 2: "^^", 1: "^", 0: "=", "-1": "_", "-2": "__" };
const NOTE_RE = /^(\^\^|\^|__|_|=)?([A-Ga-g])([,']*)/;

const mod = (n, m) => ((n % m) + m) % m;

const modeKey = (text) => {
  const t = (text || "").toLowerCase();
  if (!t) return "";
  if (t.startsWith("m") && !t.startsWith("maj") && !t.startsWith("mix")) return "min";
  const k = t.slice(0, 3);
  return k in MODE_FIFTHS ? k : "";
};

const fifthsOf = (letter, acc, mode) =>
  MAJOR_FIFTHS[letter] + 7 * acc + (MODE_FIFTHS[mode] ?? 0);

// accidentals carried by the key signature, per natural letter
const keySignature = (fifths) => {
  const sig = [0, 0, 0, 0, 0, 0, 0];
  const n = Math.min(7, Math.abs(fifths));
  const order = fifths > 0 ? SHARP_ORDER : FLAT_ORDER;
  for (let i = 0; i < n; i++) sig[order[i]] = fifths > 0 ? 1 : -1;
  return sig;
};

// "Dmin", "Bb dor", "F#" … → root + the untouched remainder of the field
const parseKey = (raw) => {
  const m = /^(\s*)([A-Ga-g])([#b♯♭]?)/.exec(raw || "");
  if (!m) return null;
  const mode = /^\s*([A-Za-z]*)/.exec(raw.slice(m[0].length));
  return {
    letter: LETTER_IDX[m[2].toUpperCase()],
    acc: m[3] === "#" || m[3] === "♯" ? 1 : m[3] === "b" || m[3] === "♭" ? -1 : 0,
    mode: modeKey(mode ? mode[1] : ""),
    rootLen: m[0].length,
    indent: m[1],
  };
};

const accText = (acc) => (acc === 1 ? "#" : acc === -1 ? "b" : "");

// Destination tonic: an enharmonic spelling with a playable key signature,
// following the requested sharp/flat preference.
const destinationRoot = (root, steps, preferSharp) => {
  const target = mod(PC[root.letter] + root.acc + steps, 12);
  const all = [];
  for (let letter = 0; letter < 7; letter++) {
    for (const acc of [0, 1, -1]) {
      if (mod(PC[letter] + acc, 12) !== target) continue;
      const fifths = fifthsOf(letter, acc, root.mode);
      const against = preferSharp ? acc < 0 : acc > 0;
      all.push({ letter, acc, fifths, score: (against ? 100 : 0) + Math.abs(fifths) });
    }
  }
  const usable = all.filter((c) => Math.abs(c.fifths) <= 7);
  const pool = usable.length ? usable : all;
  return pool.sort((a, b) => a.score - b.score)[0];
};

const runTranspose = (abc, steps, preferSharp) => {
  let src = { letter: 0, acc: 0, mode: "" }; // default C major
  let dst = destinationRoot(src, steps, preferSharp);
  let sigSrc = keySignature(fifthsOf(src.letter, src.acc, src.mode));
  let sigDst = keySignature(dst.fifths);
  let offset = mod(dst.letter - src.letter, 7);
  let barSrc = {};
  let barDst = {};

  const applyKey = (root) => {
    src = root;
    dst = destinationRoot(root, steps, preferSharp);
    sigSrc = keySignature(fifthsOf(root.letter, root.acc, root.mode));
    sigDst = keySignature(dst.fifths);
    offset = mod(dst.letter - root.letter, 7);
    barSrc = {};
    barDst = {};
  };

  const rewriteKey = (raw) => {
    const k = parseKey(raw);
    if (!k) return raw;
    applyKey(k);
    return k.indent + LETTERS[dst.letter] + accText(dst.acc) + raw.slice(k.rootLen);
  };

  // chord symbols: "Am7", "D/F#" — root (and bass note) move with the key
  const movePitchClass = (letter, acc) => {
    const newLetter = mod(letter + offset, 7);
    const target = mod(PC[letter] + acc + steps, 12);
    const d = mod(target - PC[newLetter] + 6, 12) - 6;
    if (d > 2 || d < -2) return null;
    return LETTERS[newLetter] + accText(d);
  };

  const rewriteChord = (inner) => {
    if (/^[\^_<>@]/.test(inner)) return inner; // annotation, not a chord
    const m = /^([A-G])([#b]?)(.*)$/.exec(inner);
    if (!m) return inner;
    const root = movePitchClass(LETTER_IDX[m[1]], m[2] === "#" ? 1 : m[2] === "b" ? -1 : 0);
    if (!root) return inner;
    let tail = m[3];
    const bass = /^(.*)\/([A-G])([#b]?)(.*)$/.exec(tail);
    if (bass) {
      const moved = movePitchClass(LETTER_IDX[bass[2]], bass[3] === "#" ? 1 : bass[3] === "b" ? -1 : 0);
      if (moved) tail = `${bass[1]}/${moved}${bass[4]}`;
    }
    return root + tail;
  };

  const rewriteNote = (m) => {
    const [full, accStr, letterCh, octStr] = m;
    const letter = LETTER_IDX[letterCh.toUpperCase()];
    let oct = letterCh === letterCh.toLowerCase() ? 5 : 4;
    for (const c of octStr) oct += c === "'" ? 1 : -1;

    const slot = `${letter}:${oct}`;
    const explicit = accStr ? ACC_VAL[accStr] : null;
    const effective = explicit !== null ? explicit : slot in barSrc ? barSrc[slot] : sigSrc[letter];
    if (explicit !== null) barSrc[slot] = explicit;

    const target = 12 * (oct + 1) + PC[letter] + effective + steps;
    const wanted = mod(letter + offset, 7);
    // the diatonically correct letter, unless it would need a triple accidental
    let best = null;
    for (const l of [wanted, mod(wanted + 1, 7), mod(wanted + 6, 7)]) {
      for (let o = -2; o <= 11; o++) {
        const d = target - (12 * (o + 1) + PC[l]);
        if (Math.abs(d) > 2) continue;
        const score = (l === wanted ? 0 : 100) + Math.abs(d);
        if (!best || score < best.score) best = { letter: l, oct: o, need: d, score };
      }
    }
    if (!best) return full;
    const newLetter = best.letter;
    const newOct = best.oct;
    const need = best.need;

    const newSlot = `${newLetter}:${newOct}`;
    const shown = newSlot in barDst ? barDst[newSlot] : sigDst[newLetter];
    let out = "";
    // an accidental written by the author stays written (courtesy accidentals too)
    if (explicit !== null || need !== shown) {
      out += ACC_STR[need];
      barDst[newSlot] = need;
    }
    out +=
      newOct >= 5
        ? LETTERS[newLetter].toLowerCase() + "'".repeat(newOct - 5)
        : LETTERS[newLetter] + ",".repeat(4 - newOct);
    return out;
  };

  const transposeBody = (line) => {
    let out = "";
    let i = 0;
    while (i < line.length) {
      const ch = line[i];
      if (ch === '"') {
        const end = line.indexOf('"', i + 1);
        const inner = end < 0 ? line.slice(i + 1) : line.slice(i + 1, end);
        out += `"${rewriteChord(inner)}"`;
        i = end < 0 ? line.length : end + 1;
        continue;
      }
      if (ch === "!" || ch === "+") {
        const end = line.indexOf(ch, i + 1);
        if (end > i) {
          out += line.slice(i, end + 1);
          i = end + 1;
          continue;
        }
      }
      if (ch === "[") {
        const fld = /^\[([A-Za-z]):([^\]]*)\]/.exec(line.slice(i));
        if (fld) {
          out += fld[1] === "K" ? `[K:${rewriteKey(fld[2])}]` : fld[0];
          i += fld[0].length;
          continue;
        }
      }
      if (ch === "%") {
        out += line.slice(i);
        break;
      }
      if (ch === "|" || ch === ":") {
        barSrc = {};
        barDst = {};
        out += ch;
        i++;
        continue;
      }
      const nm = NOTE_RE.exec(line.slice(i));
      if (nm) {
        out += rewriteNote(nm);
        i += nm[0].length;
        continue;
      }
      // a stray accidental not attached to a note is invalid ABC and makes
      // renderers disagree — drop it rather than carry the ambiguity over
      if (ch === "^" || ch === "_" || ch === "=") {
        i++;
        continue;
      }
      out += ch;
      i++;
    }
    return out;
  };

  return abc
    .split(/\r?\n/)
    .map((line) => {
      const field = /^\s*([A-Za-z]):(.*)$/.exec(line);
      if (field) {
        return field[1] === "K"
          ? `${line.slice(0, line.indexOf(":") + 1)}${rewriteKey(field[2])}`
          : line;
      }
      if (line.trim().startsWith("%")) return line;
      return transposeBody(line);
    })
    .join("\n");
};

// double accidentals are legal but unreadable — prefer the cleaner spelling,
// and never mix sharp keys with flat keys inside one tune (renderers get it wrong)
const mixedKeys = (abc) => {
  const accs = [...abc.matchAll(/K:\s*[A-G]([#b]?)/g)].map((m) => m[1]);
  return accs.includes("#") && accs.includes("b");
};

const messiness = (abc) =>
  (mixedKeys(abc) ? 1000 : 0) +
  (abc.match(/\^\^|__/g) || []).length * 10 +
  (abc.match(/[\^_=]/g) || []).length;

export const transposeAbcText = (abc, steps) => {
  if (!steps || !abc) return abc;
  const sharp = runTranspose(abc, steps, true);
  const flat = runTranspose(abc, steps, false);
  if (sharp === flat) return sharp;
  return messiness(flat) < messiness(sharp) ? flat : sharp;
};
